import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Input, InputNumber, Select, Button, Tag, Space, Typography,
  Alert, Form, Row, Col, Divider, App as AntApp, Flex,
} from 'antd';
import {
  SearchOutlined, CheckCircleOutlined, SwapOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text } = Typography;

interface Diferimento {
  id: string;
  lancamento_id: string;
  descricao: string;
  valor_total: number;
  valor_mensal: number;
  periodo_inicio: string;
  periodo_fim: string;
  categoria_dre: string;
  meses: number;
}

interface Lancamento {
  id: string;
  data_competencia: string;
  descricao: string;
  fornecedor_original: string;
  categoria_contaazul: string;
  valor: number;
  periodo: string;
  diferido: boolean;
}

const CATEGORIAS_DRE = [
  'Alimentação em campo', 'Aluguel de equipamentos', 'Combustíveis',
  'Diárias / Hospedagem', 'Software / Licença de Uso', 'ARTs',
  'Honorários Terceirizados', 'Remuneração de Autônomos',
  'Aluguel', 'Serviços / Assinaturas', 'Energia Elétrica',
  'Manutenção Predial', 'Computadores e Periféricos',
  'Máquinas, Equipamentos, Utensílios e Ferramentas', 'Veículos',
  'Outras Imobilizações por Aquisição', 'Empréstimos e Dívidas',
  'Depreciação', 'Amortização', 'Outras Despesas Financeiras',
];

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number) => 'R$ ' + Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

function DiferimentosInner() {
  const { message } = AntApp.useApp();
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<Lancamento[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [selecionado, setSelecionado] = useState<Lancamento | null>(null);
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [meses, setMeses] = useState<number>(12);
  const [categoriaDre, setCategoriaDre] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [diferimentos, setDiferimentos] = useState<Diferimento[]>([]);
  const [desfazendo, setDesfazendo] = useState<string | null>(null);

  useEffect(() => { carregarDiferimentos(); }, []);

  async function carregarDiferimentos() {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/lancamentos/diferimentos`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setDiferimentos(data);
    } catch {}
  }

  async function pesquisar() {
    if (!busca.trim()) return;
    setBuscando(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/lancamentos/busca?q=${encodeURIComponent(busca)}`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setResultados(data);
    } finally {
      setBuscando(false);
    }
  }

  function selecionar(l: Lancamento) {
    setSelecionado(l);
    setCategoriaDre(l.categoria_contaazul);
  }

  async function diferir() {
    if (!selecionado || !periodoInicio || meses < 1) return;
    setSalvando(true);
    try {
      const token = await getToken();
      const res = await axios.post(
        `${API_URL}/lancamentos/${selecionado.id}/diferir`,
        { periodo_inicio: periodoInicio, meses, categoria_dre: categoriaDre },
        { headers: { Authorization: token } }
      );
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      message.success(`Diferido. Valor mensal: R$ ${Math.abs(data.valorMensal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} até ${data.periodoFim}.`);
      setSelecionado(null);
      setResultados(resultados.filter(r => r.id !== selecionado.id));
      await carregarDiferimentos();
    } catch {
      message.error('Erro ao diferir lançamento.');
    } finally {
      setSalvando(false);
    }
  }

  async function desfazer(lancamentoId: string, difId: string) {
    setDesfazendo(difId);
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/lancamentos/${lancamentoId}/diferir`, { headers: { Authorization: token } });
      message.success('Diferimento desfeito.');
      await carregarDiferimentos();
    } catch {
      message.error('Erro ao desfazer diferimento.');
    } finally {
      setDesfazendo(null);
    }
  }

  return (
    <div style={{ padding: '16px 24px', maxWidth: 820, margin: '0 auto' }}>
      <Title level={4} style={{ marginTop: 0, marginBottom: 4, color: '#020D7F' }}>Diferimentos</Title>
      <Text type="secondary">
        Dilua um lançamento de grande valor ao longo do tempo, distribuindo o custo mensalmente na DRE.
      </Text>

      <Flex gap={8} style={{ marginTop: 20, marginBottom: 16 }}>
        <Input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          onPressEnter={pesquisar}
          placeholder="Buscar por descrição, fornecedor ou valor..."
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear
          style={{ flex: 1 }}
        />
        <Button type="primary" loading={buscando} onClick={pesquisar}>Buscar</Button>
      </Flex>

      {resultados.length > 0 && !selecionado && (
        <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
          {resultados.map((l, i) => (
            <div
              key={l.id}
              onClick={() => selecionar(l)}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                borderBottom: i < resultados.length - 1 ? '1px solid #F0F1F5' : 'none',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#F5F7FA')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Flex justify="space-between" align="flex-start">
                <div>
                  <Text strong style={{ fontSize: 13 }}>{l.descricao || l.fornecedor_original}</Text>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    {l.fornecedor_original} · {l.periodo} · {l.categoria_contaazul}
                  </div>
                </div>
                <Text strong style={{ color: '#B91626', fontSize: 13 }}>{fmt(l.valor)}</Text>
              </Flex>
            </div>
          ))}
        </Card>
      )}

      {selecionado && (
        <Card
          size="small"
          style={{ borderColor: '#0930D0', marginBottom: 16 }}
          title={
            <Flex justify="space-between" align="center">
              <div>
                <Text strong>{selecionado.descricao || selecionado.fornecedor_original}</Text>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2, fontWeight: 400 }}>
                  {selecionado.fornecedor_original} · {selecionado.periodo} · {fmt(selecionado.valor)}
                </div>
              </div>
              <Button size="small" icon={<SwapOutlined />} onClick={() => setSelecionado(null)}>
                Trocar
              </Button>
            </Flex>
          }
        >
          <Form layout="vertical" size="small">
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Período início (AAAA/MM)">
                  <Input
                    placeholder="Ex: 2025/01"
                    value={periodoInicio}
                    onChange={e => setPeriodoInicio(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Número de meses">
                  <InputNumber
                    value={meses}
                    onChange={(v) => setMeses(Number(v) || 0)}
                    min={1}
                    max={120}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Categoria na DRE">
              <Select
                value={categoriaDre}
                onChange={setCategoriaDre}
                options={CATEGORIAS_DRE.map(c => ({ label: c, value: c }))}
                showSearch
              />
            </Form.Item>

            {periodoInicio && meses > 0 && (
              <Alert
                type="info"
                showIcon
                message={
                  <span>Valor mensal: <strong>{fmt(selecionado.valor / meses)}</strong> por {meses} meses</span>
                }
                style={{ marginBottom: 12 }}
              />
            )}

            <Button
              type="primary"
              block
              icon={<CheckCircleOutlined />}
              loading={salvando}
              disabled={!periodoInicio || meses < 1}
              onClick={diferir}
            >
              Confirmar diferimento
            </Button>
          </Form>
        </Card>
      )}

      {diferimentos.length > 0 && (
        <>
          <Divider />
          <Title level={5} style={{ marginTop: 0, marginBottom: 12, color: '#333' }}>
            Diferimentos ativos
          </Title>
          <Card size="small" styles={{ body: { padding: 0 } }}>
            {diferimentos.map((d, i) => (
              <Flex
                key={d.id}
                justify="space-between"
                align="center"
                style={{
                  padding: '10px 14px',
                  borderBottom: i < diferimentos.length - 1 ? '1px solid #F0F1F5' : 'none',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 13 }}>{d.descricao}</Text>
                  <div style={{ marginTop: 4 }}>
                    <Space size={4}>
                      <Tag color="processing" style={{ margin: 0, fontSize: 11 }}>{d.categoria_dre}</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {d.periodo_inicio} → {d.periodo_fim} · {d.meses} meses
                      </Text>
                    </Space>
                  </div>
                  <Text style={{ fontSize: 11, color: '#555' }}>
                    Total: {fmt(d.valor_total)} · Mensal: <strong>{fmt(d.valor_mensal)}</strong>
                  </Text>
                </div>
                <Button
                  size="small"
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  loading={desfazendo === d.id}
                  onClick={() => desfazer(d.lancamento_id, d.id)}
                  title="Desfazer"
                />
              </Flex>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}

export default function Diferimentos() {
  return (
    <AntApp>
      <DiferimentosInner />
    </AntApp>
  );
}
